<script lang="ts" setup>
import {AttachmentType, MediaType, PostScope} from "~/repository/types.d";
import type {Post} from "~/repository/types";
import {useSensitiveContent} from "~/composables/useSensitiveContent";

const props = defineProps<{
  post: Post;
}>();

const route = useRoute();
const {t} = useI18n();
const isHovered = ref(false);
const isTouchDevice = ref(false);

onMounted(() => {
  isTouchDevice.value = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
});

const handleMouseOver = () => {
  if (!isTouchDevice.value) isHovered.value = true;
};

const handleMouseLeave = () => {
  if (!isTouchDevice.value) isHovered.value = false;
};

const isPinnedInUserRoute = computed(() => {
  const username = route.params.username;
  return username && props.post.is_pinned;
});


const {
  shouldHideSensitiveContent
} = useSensitiveContent(props.post);


const mediaWithoutDocs = computed(() =>
  props.post.media?.filter(m => m.type !== MediaType.DOC) ?? []
);

const mainMedia = computed(() => mediaWithoutDocs.value[0]);
</script>

<template>
  <div class="w-full h-full cursor-pointer relative"
       @mouseover="handleMouseOver"
       @mouseleave="handleMouseLeave"
       @touchstart="isHovered = true"
       @touchend="isHovered = false"
  >

    <div v-show="isHovered && !isTouchDevice && !post.is_adult"
         class="absolute inset-0 w-full h-full z-20 background-shadow-xl flex items-center justify-center">
      <div class="flex items-center gap-4 text-white font-bold">
        <div class="flex items-center gap-2">
          <IconHeart color="#ffffff" fill="#ffffff"/>
          <span>{{ post.likes_count }}</span>
        </div>
        <div class="flex items-center gap-2">
          <IconComment color="#ffffff" fill="#ffffff"/>
          <span>{{ post.comments_count }}</span>
        </div>
      </div>
    </div>


    <div v-show="!isHovered"
         class="absolute left-0 right-0 top-0 h-9 w-full z-20 background-shadow-lg flex items-center justify-center">
    </div>

    <div v-if="shouldHideSensitiveContent"
         class="absolute inset-0 z-30 flex flex-col items-center justify-center background-shadow-xl backdrop-blur-2xl">
      <div class="text-center p-6">
        <svg aria-label="Ukryte słowa" width="30px" height="30px" role="img" viewBox="0 0 24 24" fill="#ffffff">
          <path
            d="M2.936 8.014A12.792 12.792 0 0 0 .559 11.82a1 1 0 0 0 1.881.677 10.987 10.987 0 0 1 1.988-3.15 1 1 0 1 0-1.492-1.332Zm20.271 13.779-5.916-5.916a4.969 4.969 0 0 0 .133-.582.983.983 0 0 0-1.107-1.108 3.315 3.315 0 0 1-.678.038l-3.366-3.366a3.3 3.3 0 0 1 .036-.676.99.99 0 0 0-1.134-1.107 4.623 4.623 0 0 0-.553.132L8.13 6.717a10.395 10.395 0 0 1 13.429 5.779 1 1 0 0 0 1.881-.677C23.413 11.74 20.542 4 12 4a12.104 12.104 0 0 0-5.367 1.22L2.207.792A1 1 0 0 0 .793 2.207l21 21a1 1 0 0 0 1.414-1.414ZM11.382 14.9l-3.044-3.03a1.005 1.005 0 0 0-1.636.326 5.495 5.495 0 0 0 1.31 6.074 5.495 5.495 0 0 0 6.075 1.31 1.005 1.005 0 0 0 .325-1.636Z"/>
        </svg>
      </div>
    </div>

    <!-- Main media preview -->
    <div v-if="mainMedia" class="w-full pt-[155.6%] relative">
      <div class="absolute inset-0">
        <NuxtImg
          v-if="mainMedia.type === MediaType.IMAGE"
          :src="mainMedia.url"
          decoding="async"
          class="w-full h-full object-cover"
        />
        <NuxtImg
          v-else-if="mainMedia.type === MediaType.VIDEO && mainMedia.poster"
          :src="mainMedia.poster"
          decoding="async"
          class="w-full h-full object-cover"
        />
        <video
          v-else-if="mainMedia.type === MediaType.VIDEO"
          class="w-full h-full object-cover"
          playsinline
          x5-playsinline="true"
          webkit-playsinline="true"
          preload="metadata"
          :src="mainMedia.url + '#t=3'"
        />
      </div>
    </div>

    <div v-else-if="post.attachments" class="bg-gray-900 w-full pt-[155.6%] relative">
      <div v-for="attachment in post.attachments"
           :key="attachment.id"
           class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white font-bold">
        <div v-if="attachment.type === AttachmentType.EXTERNAL_LINK"
             class="flex flex-col items-center justify-center">
          <ClientOnly>
            <IconLink size="40px"/>
          </ClientOnly>
          <span class="text-white font-semibold text-xl mt-2">Link</span>
        </div>
        <div v-else-if="attachment.type === AttachmentType.POLL"
             class="flex flex-col items-center justify-center">
          <ClientOnly>
            <IconPoll size="50px"/>
          </ClientOnly>
          <span class="text-white font-semibold text-xl">Ankieta</span>
        </div>
      </div>
    </div>

    <!-- Icons and indicators -->
    <div v-if="isPinnedInUserRoute" class="absolute top-2 right-2 text-white">
      <ClientOnly>
        <IconFillPin size="22px"/>
      </ClientOnly>
    </div>
    <div v-if="mediaWithoutDocs.length === 1 && mainMedia?.type === MediaType.VIDEO && !isPinnedInUserRoute"
         class="absolute top-2 right-2 text-white">
      <ClientOnly>
        <IconVideo size="22px"/>
      </ClientOnly>
    </div>
    <div v-if="mediaWithoutDocs.length > 1 && !isPinnedInUserRoute"
         class="absolute top-2 right-2 text-white">
      <ClientOnly>
        <IconMoreMedia size="22px"/>
      </ClientOnly>
    </div>

    <div v-if="post.scope === PostScope.SUBSCRIBERS_ONLY"
         class="absolute top-2 left-2 text-white rounded-md bg-gradient-button flex items-center gap-1 py-0.5 px-1.5 z-30">
      <ClientOnly>
        <IconCrown size="14px"/>
      </ClientOnly>
      <span class="text-xs font-semibold">Exclusive</span>
    </div>

    <div v-if="post.price.amount > 0 && post.scope !== PostScope.SUBSCRIBERS_ONLY"
         class="absolute top-2 left-2 flex items-center justify-center text-white rounded-md bg-gradient-button gap-1 py-0.5 px-1.5 z-30">
      <ClientOnly>
        <Icon class="w-3.5 h-3.5 stroke-[2.5px]" name="arcticons:moneytracker" size="18px"/>
      </ClientOnly>
      <span class="text-xs font-semibold">Paid</span>
    </div>
  </div>
</template>
